import { isValueDifferent } from "@/lib/utils/isValueDifferent";
import { cn } from "@/lib/utils";
import React, {
  createContext,
  PropsWithChildren,
  useContext,
  useMemo,
} from "react";
import { useFormContext, useFormState, useWatch } from "react-hook-form";

interface FormTableCellDirtyContextValue {
  /** Baseline values for comparison (synced from query, updated on save) */
  baseline: object;
  /** Key in form that holds the record map. Defaults to "entries". */
  recordKey: string;
}

const FormTableCellDirtyContext =
  createContext<FormTableCellDirtyContextValue | null>(null);

export function useFormTableCellDirtyContext() {
  return useContext(FormTableCellDirtyContext);
}

const DEFAULT_RECORD_KEY = "entries";

type FormTableCellDirtyProviderProps = Omit<
  FormTableCellDirtyContextValue,
  "recordKey"
> &
  PropsWithChildren & { recordKey?: string };

export function FormTableCellDirtyProvider({
  children,
  baseline,
  recordKey = DEFAULT_RECORD_KEY,
}: FormTableCellDirtyProviderProps) {
  const value = useMemo(() => ({ baseline, recordKey }), [baseline, recordKey]);
  return (
    <FormTableCellDirtyContext.Provider value={value}>
      {children}
    </FormTableCellDirtyContext.Provider>
  );
}

/**
 * Edited vs baseline: narrow amber ring when unfocused; thicker on focus.
 * focus-visible:ring-amber-400 overrides Input’s focus ring so priority is error > edited > default focus.
 * ring-inset keeps it visible in cells with overflow:hidden (e.g. truncate).
 */
const dirtyHighlightClassName =
  "ring-1 ring-inset ring-amber-400 ring-offset-0 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-amber-400";

interface DirtyAwareCellProps extends PropsWithChildren {
  /** Form field path (e.g. "entries.xxx.deduction") */
  fieldPath: string;
}

/**
 * Injects dirty highlight onto the input/checkbox when its value differs from
 * baseline. Passes className to the child—highlight appears on the input itself.
 * Suppressed while the field has a validation error so error styles stay clear.
 */
export function DirtyAwareCell({ fieldPath, children }: DirtyAwareCellProps) {
  const ctx = useFormTableCellDirtyContext();
  const currentValue = useWatch({ name: fieldPath });
  const { getFieldState, control } = useFormContext();
  const fieldFormState = useFormState({
    control,
    name: fieldPath,
    exact: true,
  });
  const hasError = !!getFieldState(fieldPath, fieldFormState).error;

  const isDirty = useMemo(() => {
    if (!ctx) return false; // not inside FormTableCellDirtyProvider

    const parts = fieldPath.split(".");
    if (parts.length < 3) return false;

    // Walk the full path through the baseline object (supports 3+ levels,
    // e.g. "entries.rowId.field" or "stores.storeId.itemId.field").
    let baselineValue: unknown = ctx.baseline;
    for (const part of parts) {
      if (baselineValue == null || typeof baselineValue !== "object")
        return false;
      baselineValue = (baselineValue as Record<string, unknown>)[part];
    }

    return isValueDifferent(baselineValue, currentValue);
  }, [ctx, fieldPath, currentValue]);

  const child = React.Children.only(children);
  if (!React.isValidElement<{ className?: string }>(child)) {
    return children;
  }

  const showDirtyHighlight = isDirty && !hasError;

  return React.cloneElement(
    child as React.ReactElement<{ className?: string }>,
    {
      className: cn(
        child.props.className,
        showDirtyHighlight && dirtyHighlightClassName,
      ),
    },
  );
}
